import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import LoadingButton from '@mui/lab/LoadingButton';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { FlexColumn } from 'src/components/flex';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import { useNotifications } from 'src/notifications';
import { isStandardErrorResponse } from 'src/api/common';
import {
    type IContact,
    type IProperty,
    resolvePropertyContacts,
    useContacts,
    useProperties,
    useSetContactProperties,
} from 'src/services/api';
import { contactDisplayName } from 'src/components/edit-contacts-modal/EditContactsModal';

import styles from 'src/components/create-or-update-property-modal/PropertyContactsSection.module.css';
import modalStyles from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal.module.css';

interface Props {
    property: IProperty;
}

export default function PropertyContactsSection({ property }: Props) {
    const { addNotification } = useNotifications();
    const { data: contacts, isLoading } = useContacts();
    const { data: properties } = useProperties();
    const { mutateAsync: setContactProperties } = useSetContactProperties();

    // draft switch positions keyed by contact id; only differences from the
    // effective (own or inherited) state count as unsaved changes
    const [edits, setEdits] = useState<Record<number, boolean>>({});
    const [isSaving, setIsSaving] = useState(false);

    const isContactLinked = (contact: IContact): boolean =>
        contact.property_ids.includes(property.id);

    // the switches reflect the property's effective contacts: with no own
    // links these are inherited from the nearest ancestor with any linked,
    // resolved locally from the cached contacts and properties lists
    const resolvedContactIds = contacts === undefined || properties === undefined ?
        undefined :
        new Set(resolvePropertyContacts(contacts, properties, property.id).map((contact) => contact.id));
    const isContactEffective = (contact: IContact): boolean =>
        resolvedContactIds === undefined ? isContactLinked(contact) : resolvedContactIds.has(contact.id);

    const isContactOn = (contact: IContact): boolean =>
        edits[contact.id] ?? isContactEffective(contact);

    const changedContacts = (contacts ?? []).filter((contact) =>
        edits[contact.id] !== undefined && edits[contact.id] !== isContactEffective(contact)
    );
    const hasUnsavedChanges = changedContacts.length > 0;

    const applyChanges = async () => {
        if (changedContacts.length === 0) {
            addNotification('Contacts are already up to date', 'success');
            return;
        }

        // materialize the visible switch positions as this property's own
        // links, which override inheritance; inherited contacts left on
        // become direct links so the edited set survives as a whole
        const updates = (contacts ?? []).filter(
            (contact) => isContactOn(contact) !== isContactLinked(contact)
        );

        setIsSaving(true);
        try {
            await Promise.all(updates.map((contact) => {
                const newPropertyIds = isContactOn(contact) ?
                    [...contact.property_ids, property.id] :
                    contact.property_ids.filter((propertyId) => propertyId !== property.id);
                return setContactProperties({ id: contact.id, property_ids: newPropertyIds });
            }));
            setEdits({});
            addNotification('Contacts updated', 'success');
        } catch (error) {
            if (isStandardErrorResponse(error)) {
                addNotification(error.msg || 'Failed to update contacts', 'error');
            }
        } finally {
            setIsSaving(false);
        }
    };

    let content: React.ReactNode = null;
    if (!isLoading && contacts?.length === 0) {
        content = (
            <Alert severity="info" data-testid="property-contacts-empty-alert">
                Your team does not have any contacts yet.
                Create them with the Contacts button at the top of the properties page.
            </Alert>
        );
    } else if (contacts !== undefined) {
        content = (
            <>
                <FlexColumn rowGap={1}>
                    {contacts.map((contact) => (
                        <div key={contact.id} className={styles.contactRow}>
                            <div className={styles.contactMain}>
                                <ProfileImageRounded userName={contactDisplayName(contact)} width={32} height={32} />
                                <div className={styles.contactInfo}>
                                    <Typography variant="body2">{contactDisplayName(contact)}</Typography>
                                    {contact.name && (
                                        <Typography variant="caption" color="text.secondary">{contact.email}</Typography>
                                    )}
                                </div>
                            </div>
                            <Switch
                                checked={isContactOn(contact)}
                                onChange={(_event, checked) =>
                                    setEdits((prev) => ({ ...prev, [contact.id]: checked }))
                                }
                                disabled={isSaving}
                                size="small"
                                data-testid={`property-contact-switch-${contact.id}`}
                            />
                        </div>
                    ))}
                </FlexColumn>
                <div className={styles.actions}>
                    <LoadingButton
                        className={modalStyles.sectionSaveButton}
                        variant="contained"
                        size="small"
                        loading={isSaving}
                        onClick={applyChanges}
                        data-testid="save-property-contacts-button"
                    >
                        Save
                    </LoadingButton>
                </div>
            </>
        );
    }

    return (
        <FlexColumn rowGap={2}>
            <div className={styles.titleColumn}>
                <div className={styles.titleRow}>
                    <Typography variant="body2" fontWeight={500}>Contacts</Typography>
                    {hasUnsavedChanges && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            data-testid="property-contacts-dirty-indicator"
                        >
                            Unsaved changes
                        </Typography>
                    )}
                </div>
            </div>
            <Alert severity="info" data-testid="property-contacts-info-alert">
                Contacts that are switched on are included in the tour invitation email whenever a tour is scheduled for this property.
            </Alert>
            {content}
        </FlexColumn>
    );
}
